/**
 * Gallery resource utilities — resolves ImageGalleryV2 props from SFCC product data.
 *
 * Entry points: resolveGalleryResource(), resolveGalleryResourceCombined()
 * Layer builders for building GalleryLayer arrays: buildLayerFrom*, buildLayersFromSelections()
 */

import {mergePreselectedSelectionsFromProduct} from '@amblique/king-living/app/utils/customize-selections';
import {shouldApplyGalleryLayerPriority} from '@amblique/king-living/app/utils/gallery-state-utils';
import {
  DEFAULT_CYLINDO_CUSTOMER_ID,
  buildCylindoSnapshotImageUrl,
  findVariantCylindoFeature,
  findVariantEntry,
  mergeCylindoConfiguration,
  normalizeCylindoFeatureToString,
  resolveCylindoProductCode,
  parseProcessedImageGroups,
  parseCylindoFeatureAttribute,
} from '@amblique/king-living/app/utils/cylindo-params';
import {DISPLAY_VIEWTYPE_PRIORITY} from '@amblique/king-living/app/utils/product-display-image';

const LARGE = 'large';

// ── GalleryLayer ───────────────────────────────────────────────────────────────

/**
 * @typedef {Object} GalleryLayer
 * @property {string|null} [cylindoFeature] - Raw cylindoFeature string (e.g. "UPHOLSTERY:BX-11").
 * @property {Array|null}  [pimImages]      - Lifestyle stills from pimlyLifeStyleImages (not catalog pimImages).
 * @property {string|null} [video]          - Dedicated video URL (exclusive over pimImages; live-preview and committed option layers).
 */

/**
 * @param {Object|null|undefined} product
 * @returns {string|null}
 */
function getProductVideoUrl(product) {
  const raw = product?.c_video ?? product?.video ?? null;
  const url = raw != null ? String(raw).trim() : '';

  return url || null;
}

/** Stable sort key — ephemeral preview is latest touch (not Date.now() each render). */
export const EPHEMERAL_GALLERY_TOUCH_PRIORITY = Number.MAX_SAFE_INTEGER - 2;

/** Cover sub-view open — cover re-focus beats stale option preview (E-V4b). */
export const COVER_SUBVIEW_TOUCH_PRIORITY = Number.MAX_SAFE_INTEGER - 1;

/**
 * @param {GalleryLayer|null|undefined} layer
 * @returns {boolean}
 */
export function layerHasHeroResourceSignal(layer) {
  if (!layer) return false;

  if (layer.video != null && String(layer.video).trim() !== '') return true;
  if (Array.isArray(layer.pimImages) && layer.pimImages.length > 0) return true;

  return layer.cylindoFeature != null && layer.cylindoFeature !== '';
}

/**
 * @param {Array|null|undefined} pimImages
 * @returns {string|null}
 */
function extractDedicatedVideoUrlFromPimImages(pimImages) {
  if (!Array.isArray(pimImages)) return null;

  const videoSlide = pimImages.find((img) => img?.mediaType === 'video');
  const raw = videoSlide?.videoUrl ?? videoSlide?.link ?? null;
  const url = raw != null ? String(raw).trim() : '';

  return url || null;
}

/**
 * Cover touch layer from selection metadata (variationValues) — no cover SCAPI product.
 * Same shape as option layers for last-layer-wins hero resolution.
 *
 * @param {Object|null|undefined} masterProduct
 * @param {Object|null|undefined} coverSelection
 * @returns {GalleryLayer}
 */
export function buildCoverGalleryLayerFromSelection(masterProduct, coverSelection) {
  if (!masterProduct || !coverSelection) {
    return {cylindoFeature: null, pimImages: null, video: null};
  }

  const variationValues =
    coverSelection.variationValues && typeof coverSelection.variationValues === 'object' ?
      coverSelection.variationValues :
      {};
  const keys = Object.keys(variationValues).filter(
    (k) => variationValues[k] != null && variationValues[k] !== ''
  );

  if (keys.length === 0) {
    return {cylindoFeature: null, pimImages: null, video: null};
  }

  const pimImages = findVariantPimImages(masterProduct, variationValues);
  const video = extractDedicatedVideoUrlFromPimImages(pimImages);
  const cylindoFeature = normalizeCylindoFeatureToString(
    findVariantCylindoFeature(masterProduct, variationValues)
  );

  return {
    cylindoFeature: cylindoFeature || null,
    pimImages: pimImages ?? null,
    video,
  };
}

/**
 * Unified hero layer stack: cover, option/add-on slots, and ephemeral preview are equal
 * touch layers ordered by `selectedAt`.
 * - Exclusive hero (video/PIM): last layer wins in {@link finalizeHeroGalleryResource}.
 * - Cylindo: all layers merge in {@link resolveGalleryResourceCombined} (master → variant attrs → layers).
 *
 * @param {Object} params
 * @param {Object|null|undefined} params.masterProduct
 * @param {Array<{
 *   selectedAt: number,
 *   channel: 'cover'|'product',
 *   coverSelection?: Object,
 *   slot?: Object,
 *   previewProduct?: Object,
 * }>} [params.touchEntries]
 * @returns {GalleryLayer[]}
 */
export function buildHeroGalleryLayerStack({masterProduct, touchEntries = []}) {
  const layerEntries = [];

  touchEntries.forEach((entry) => {
    let layer = null;

    if (entry.channel === 'cover' && entry.coverSelection) {
      layer = buildCoverGalleryLayerFromSelection(masterProduct, entry.coverSelection);
    } else if (entry.previewProduct) {
      layer = buildLayerFromProduct(entry.previewProduct);
    } else if (entry.slot) {
      layer = buildLayerFromSelection(entry.slot);
    }

    if (!layerHasHeroResourceSignal(layer)) return;

    layerEntries.push({
      layer,
      selectedAt: typeof entry.selectedAt === 'number' ? entry.selectedAt : 0,
    });
  });

  layerEntries.sort((a, b) => a.selectedAt - b.selectedAt);

  return layerEntries.map((entry) => entry.layer);
}

/**
 * Dedicated `video` custom attribute → single exclusive slide for ImageGalleryV2.
 *
 * @param {string} videoUrl
 * @param {Object|null|undefined} [altSource]
 * @returns {Array<{mediaType: 'video', videoUrl: string, alt: string, title: string}>}
 */
function buildExclusiveVideoPimImages(videoUrl, altSource) {
  const alt = altSource?.name ?? '';

  return [{mediaType: 'video', videoUrl, alt, title: alt}];
}

/**
 * Last-layer-wins for dedicated video (strict — same as {@link resolveLastLayerPimImages}).
 * Only the final layer's `video` counts; earlier layers are ignored (no backward scan).
 *
 * @param {GalleryLayer[]} layers
 * @returns {string|null}
 */
function resolveDedicatedVideoUrlFromLayers(layers) {
  const lastLayer = layers[layers.length - 1];
  const url = lastLayer?.video;

  if (url == null || String(url).trim() === '') {
    return null;
  }

  return String(url).trim();
}

/**
 * Build a GalleryLayer from a committed option slot selection.
 * @param {Object|null} selection
 * @returns {GalleryLayer}
 */
export function buildLayerFromSelection(selection) {
  const video = selection?.video != null ? String(selection.video).trim() : '';

  return {
    cylindoFeature: selection?.cylindoFeature ?? null,
    pimImages: Array.isArray(selection?.pimImages) && selection.pimImages.length > 0 ?
      selection.pimImages :
      null,
    // Preserve the option's dedicated video so the committed gallery layer keeps
    // parity with the live-preview `buildLayerFromProduct` (video wins in the hero).
    video: video || null,
  };
}

/**
 * Build a GalleryLayer from a product object (typically a live-preview product).
 * @param {Object|null} product
 * @returns {GalleryLayer}
 */
export function buildLayerFromProduct(product) {
  if (!product) return {cylindoFeature: null, pimImages: null, video: null};

  const imageGroups = parseProcessedImageGroups(product);
  const heroGroup = imageGroups.find((g) => g.viewType === LARGE && !g.variationAttributes) ?? null;
  const pimImages =
    heroGroup?.imageSource === 'pim' && heroGroup.images?.length > 0 ? heroGroup.images : null;

  const rawFeature = product?.c_cylindoFeature ?? product?.cylindoFeature ?? null;

  return {
    cylindoFeature: normalizeCylindoFeatureToString(rawFeature),
    pimImages,
    video: getProductVideoUrl(product),
  };
}

/**
 * Build layers from a draft-selections object.
 * Skips 'cover' (handled via selectedVariationAttributes).
 * Slots with no Cylindo feature, no PIM images and no video are omitted.
 *
 * Layers are ordered by selection recency (`slot.selectedAt`, ascending) so the
 * most-recently-changed option ends up LAST. Downstream resolvers apply
 * last-layer-wins for the exclusive hero (video/PIM) and later-wins-per-key for
 * the Cylindo feature merge, so ordering by recency means updating ANY option —
 * not only the last catalog slot — surfaces that latest choice in the hero.
 * Slots without a recency marker (predefined sets, legacy/persisted selections)
 * fall back to catalog order via a stable sort, preserving prior behavior.
 *
 * @param {Object|null} draftSelections - { cover: {...}, [containerId]: Array<slot> }
 * @returns {GalleryLayer[]}
 */
export function buildLayersFromSelections(draftSelections) {
  if (!draftSelections || typeof draftSelections !== 'object') return [];

  const entries = [];

  Object.entries(draftSelections).forEach(([key, value]) => {
    if (key === 'cover' || !Array.isArray(value)) return;

    value.forEach((slot) => {
      if (!slot) return;

      if (slot.isAutoSelected) return;

      const hasCylindo = slot.cylindoFeature != null && slot.cylindoFeature !== '';
      const hasPim = Array.isArray(slot.pimImages) && slot.pimImages.length > 0;
      const hasVideo = slot.video != null && String(slot.video).trim() !== '';

      if (hasCylindo || hasPim || hasVideo) {
        entries.push({
          layer: buildLayerFromSelection(slot),
          selectedAt: typeof slot.selectedAt === 'number' ? slot.selectedAt : 0,
        });
      }
    });
  });

  // Stable sort (Array.prototype.sort is stable) — most-recently-changed last.
  entries.sort((a, b) => a.selectedAt - b.selectedAt);

  return entries.map((entry) => entry.layer);
}

/**
 * Cover lifestyle stills from `c_allVariantsData[].pimImages` (pimlyLifeStyleImages; null if inherited).
 *
 * @param {Object|null} masterProduct
 * @param {Object} selectedVariationAttributes
 * @returns {Array|undefined}
 */
function findVariantPimImages(masterProduct, selectedVariationAttributes) {
  const entry = findVariantEntry(masterProduct, selectedVariationAttributes);

  return Array.isArray(entry?.pimImages) && entry.pimImages.length > 0 ?
    entry.pimImages :
    undefined;
}

/**
 * Master stills for the PDP hero carousel.
 * View-type order matches {@link DISPLAY_VIEWTYPE_PRIORITY}: `large` → `hi-res` → `medium` → `small`.
 * PIM photography wins within each type. When `allowCatalogFallback` is true (simple / FG —
 * Cylindo viewer off), catalog or SFRA-synthesised groups are accepted so a hi-res-only
 * assignment still renders.
 *
 * @param {Array} imageGroups
 * @param {{allowCatalogFallback?: boolean}} [options]
 * @returns {Array|undefined}
 */
function findMasterHeroStillImages(imageGroups, {allowCatalogFallback = false} = {}) {
  const masters = (imageGroups || []).filter(
    (g) => !g.variationAttributes && Array.isArray(g.images) && g.images.length > 0
  );

  for (const viewType of DISPLAY_VIEWTYPE_PRIORITY) {
    const ofType = masters.filter((g) => g.viewType === viewType);
    const pim = ofType.find((g) => g.imageSource === 'pim');

    if (pim) return pim.images;
    if (allowCatalogFallback && ofType[0]) return ofType[0].images;
  }

  return undefined;
}

// PIM of the last layer only. "Last layer" = the product being previewed right now
// (the cover/option just interacted with), not an earlier one — so switching to a
// Cylindo cover/option after a PIM one won't keep showing the earlier PIM.
function resolveLastLayerPimImages(layers) {
  const lastLayer = layers[layers.length - 1];

  return lastLayer?.pimImages?.length > 0 ? lastLayer.pimImages : undefined;
}

function resolveGalleryCustomerId(masterProduct, allowCylindo) {
  if (!allowCylindo || !masterProduct) return undefined;

  const rawCustomer = masterProduct?.c_cylindoCustomerId || masterProduct?.cylindoCustomerId;

  if (rawCustomer) {
    return String(rawCustomer).trim();
  }

  return resolveCylindoProductCode(masterProduct) ? DEFAULT_CYLINDO_CUSTOMER_ID : undefined;
}

function buildPimGalleryResource(pimImages, isReady = true) {
  return {
    pimImages,
    cylindoId: undefined,
    cylindoFeatures: [],
    customerId: undefined,
    isReady,
  };
}

function buildCylindoGalleryResource(cylindoId, cylindoFeatures, customerId, isReady = true) {
  return {
    pimImages: undefined,
    cylindoId,
    cylindoFeatures,
    customerId,
    isReady,
  };
}

function buildEmptyHeroGalleryResource(cylindoFeatures = [], isReady = true) {
  return {
    pimImages: undefined,
    cylindoId: undefined,
    cylindoFeatures,
    customerId: undefined,
    isReady,
  };
}

/**
 * PDP hero main image. Tier order (TSD: video > pimImages > cylindoFeature):
 *   0. Dedicated video
 *   1. Last preview layer PIM — DRAFT only (currently previewed product; KINDW-1794)
 *   2/3. Cover tier — draft: PIM → configured Cylindo; committed (`preferVariantCylindo`):
 *        configured Cylindo → PIM. Configured Cylindo always uses **master** product code
 *        + combined features (DE-790). Cover/option `cylindoId` is ignored. The configured
 *        render only counts when the cover contributes a `cylindoFeature`; without one it
 *        would be the default upholstery and must not outrank the cover's own PIM.
 *   4. Master PIM (page-load default / fallback)
 *   5. Master cylindoId (with combined variant/option features)
 *   6. None → "Image not available"
 *
 * @returns {{ pimImages, cylindoId, cylindoFeatures, customerId, isReady }}
 */
function finalizeHeroGalleryResource({
  allowCylindo,
  applyLayerPriority,
  cylindoFeatures,
  customerId,
  dedicatedVideoUrl,
  hasVariantCylindoFeature = false,
  isReady,
  keys,
  layers,
  masterProduct,
  variantPimImages,
  masterPimImages,
  preferVariantCylindo = false,
}) {
  if (dedicatedVideoUrl) {
    return buildPimGalleryResource(
      buildExclusiveVideoPimImages(dedicatedVideoUrl, masterProduct),
      isReady
    );
  }

  const resolvedCustomerId =
    customerId ?? resolveGalleryCustomerId(masterProduct, allowCylindo);

  const hasSelectedVariant = keys.length > 0;

  // 1. Last preview layer PIM (draft only) — the currently previewed product's own PIM (KINDW-1794).
  // Committed set/customize skips this so the combined Cylindo wins over the last layer.
  if (!preferVariantCylindo && applyLayerPriority && layers.length > 0) {
    const layerPimImages = resolveLastLayerPimImages(layers);

    if (layerPimImages?.length > 0) {
      return buildPimGalleryResource(layerPimImages, isReady);
    }
  }

  // 2/3. Cover tier. Cylindo always uses master product code + combined features (DE-790).
  // Committed → configured Cylindo first; draft → cover PIM first.
  const variantPimResource =
    hasSelectedVariant && variantPimImages?.length > 0 ?
      buildPimGalleryResource(variantPimImages, isReady) :
      null;

  const masterCode = resolveCylindoProductCode(masterProduct);

  const masterCylindoResource =
    allowCylindo && masterCode ?
      buildCylindoGalleryResource(
        masterCode,
        cylindoFeatures,
        resolvedCustomerId ?? DEFAULT_CYLINDO_CUSTOMER_ID,
        isReady
      ) :
      null;

  // Master code alone renders the default upholstery, so it is only "configured" once the
  // cover supplies a feature. Otherwise the cover PIM is the only view of the selection.
  const configuredCylindoResource =
    hasSelectedVariant && hasVariantCylindoFeature && masterCylindoResource ?
      masterCylindoResource :
      null;

  const variantFirst = preferVariantCylindo ? configuredCylindoResource : variantPimResource;
  const variantSecond = preferVariantCylindo ? variantPimResource : configuredCylindoResource;

  // Draft touch stack: last layer owns exclusive video/PIM (tiers 0–1). Skip variant cover PIM
  // only — it would stale-beat a newer option preview. Combined Cylindo still resolves.
  const layerStackActive =
    applyLayerPriority && layers.length > 0 && !preferVariantCylindo;

  if (!layerStackActive) {
    if (variantFirst) return variantFirst;
    if (variantSecond) return variantSecond;
  } else if (masterCylindoResource && cylindoFeatures.length > 0) {
    return masterCylindoResource;
  }

  // 4. Master PIM → PIM carousel (page-load default / final fallback)
  if (masterPimImages?.length > 0) {
    return buildPimGalleryResource(masterPimImages, isReady);
  }

  // 5. Master cylindoId → 3D viewer (master)
  if (masterCylindoResource) {
    return masterCylindoResource;
  }

  // 6. Nothing configured
  return buildEmptyHeroGalleryResource(cylindoFeatures, isReady);
}

// ── Single-product resolver ────────────────────────────────────────────────────

/**
 * Resolves ImageGalleryV2 props for a **single standalone product** (no layers, no
 * master+variant combination). Use this when displaying one product in isolation —
 * e.g. an optional add-on product when "View details" is clicked.
 *
 * Priority: dedicated `video` attribute → pimImages carousel → Cylindo → SFCC imageGroups
 *
 * @param {Object|null} product
 * @param {{ allowCylindo?: boolean }} [options={}]
 * @returns {{ pimImages, cylindoId, cylindoFeatures, customerId, isReady }}
 */
export function resolveGalleryResourceForProduct(product, {allowCylindo = true} = {}) {
  if (!product) return {cylindoFeatures: [], isReady: false};

  const pimResult = (pimImages) => ({pimImages, cylindoId: undefined, cylindoFeatures: [], customerId: undefined, isReady: true});

  const dedicatedVideoUrl = getProductVideoUrl(product);

  if (dedicatedVideoUrl) {
    return pimResult(buildExclusiveVideoPimImages(dedicatedVideoUrl, product));
  }

  const processedGroups = parseProcessedImageGroups(product);
  const processedHeroImages = findMasterHeroStillImages(processedGroups, {
    allowCatalogFallback: !allowCylindo,
  });

  if (processedHeroImages?.length > 0) {
    return pimResult(processedHeroImages);
  }
  if (allowCylindo) {
    const cylindoId = resolveCylindoProductCode(product);

    if (cylindoId) {
      const rawCustomer = product?.c_cylindoCustomerId || product?.cylindoCustomerId;

      return {
        pimImages: undefined,
        cylindoId,
        cylindoFeatures: parseCylindoFeatureAttribute(product),
        customerId: rawCustomer ? String(rawCustomer).trim() : DEFAULT_CYLINDO_CUSTOMER_ID,
        isReady: true,
      };
    }
  }

  // 4. SFCC imageGroups fallback — large, then hi-res (catalog often assigns hi-res only)
  const catalogHeroImages = findMasterHeroStillImages(product.imageGroups, {
    allowCatalogFallback: true,
  });

  if (catalogHeroImages?.length > 0) return pimResult(catalogHeroImages);

  return {pimImages: undefined, cylindoId: undefined, cylindoFeatures: [], customerId: undefined, isReady: false};
}

/** Desktop parity — mount ImageGalleryV2 so it renders the "Image not available" placeholder. */
export const VIEW_DETAILS_EMPTY_GALLERY = Object.freeze({
  customerId: undefined,
  cylindoFeatures: [],
  cylindoId: undefined,
  isReady: true,
  pimImages: undefined,
});

/**
 * View-details fallback when {@link resolveGalleryResourceForProduct} finds no hero carousel.
 * Prefers large / main image sources before list-tile swatch URLs.
 *
 * @param {Object|null} product
 * @returns {Array|null}
 */
function buildViewDetailsThumbnailPimImages(product) {
  if (!product) return null;

  const layer = buildLayerFromProduct(product);

  if (layer.pimImages?.length) return layer.pimImages;

  const processedGroups = parseProcessedImageGroups(product);
  const processedHero = findMasterHeroStillImages(processedGroups, {allowCatalogFallback: true});

  if (processedHero?.length) {
    return processedHero;
  }

  const catalogHero = findMasterHeroStillImages(product.imageGroups, {allowCatalogFallback: true});

  if (catalogHero?.length) {
    return catalogHero;
  }

  const firstImageGroup = product.imageGroups?.find(
    (g) => Array.isArray(g.images) && g.images.length > 0
  );
  const firstImage = firstImageGroup?.images?.[0];
  const url =
    (Array.isArray(product.c_pimlyConfigurationImage) && product.c_pimlyConfigurationImage[0]) ||
    firstImage?.disBaseLink ||
    firstImage?.link ||
    product.image?.disBaseLink ||
    product.image?.link ||
    product.c_pimlyConfigurationImageSwatch ||
    null;

  if (!url) return null;

  const alt = product.name ?? '';

  return [{disBaseLink: url, link: url, alt, title: alt}];
}

/**
 * Customize / Simple PDP "View details" overlay — show only the clicked option's media.
 * Never falls back to draft selected-option layers or master hero.
 * When nothing is available, returns {@link VIEW_DETAILS_EMPTY_GALLERY} so ImageGalleryV2
 * renders the same "Image not available" placeholder as desktop inline view details.
 *
 * @param {Object|null} viewDetailsProduct
 * @param {Object|null} masterProduct
 * @param {Object} [selectedVariationAttributes]
 * @returns {ReturnType<typeof resolveGalleryResourceForProduct>}
 */
export function resolveViewDetailsOptionGalleryResource(
  viewDetailsProduct,
  masterProduct,
  selectedVariationAttributes = {}
) {
  if (!viewDetailsProduct) return VIEW_DETAILS_EMPTY_GALLERY;

  const viewedLayer = buildLayerFromProduct(viewDetailsProduct);
  const standalone = resolveGalleryResourceForProduct(viewDetailsProduct, {allowCylindo: false});

  if (getProductVideoUrl(viewDetailsProduct)) {
    return standalone;
  }

  // Only the option's own PIM photography outranks the configured render. Catalog groups
  // are stills off the option's own Cylindo code, so they stay behind the master-code +
  // option-feature branch below (DE-790).
  const optionPimImages = findMasterHeroStillImages(parseProcessedImageGroups(viewDetailsProduct));

  if (optionPimImages?.length > 0) {
    return buildPimGalleryResource(optionPimImages);
  }

  if (viewedLayer.cylindoFeature && masterProduct && resolveCylindoProductCode(masterProduct)) {
    return resolveGalleryResource({
      allowCylindo: true,
      layers: [viewedLayer],
      masterProduct,
      previewProduct: viewDetailsProduct,
      selectedVariationAttributes,
    });
  }

  if (standalone.isReady && standalone.pimImages?.length) {
    return standalone;
  }

  const thumbPim = buildViewDetailsThumbnailPimImages(viewDetailsProduct);

  if (thumbPim?.length) {
    return {
      pimImages: thumbPim,
      cylindoId: undefined,
      cylindoFeatures: [],
      customerId: undefined,
      isReady: true,
    };
  }

  return VIEW_DETAILS_EMPTY_GALLERY;
}

// ── Main function (master + layers) ───────────────────────────────────────────

/**
 * PDP / ReviewSection entry — layer priority only after customize/set/option/cover interaction.
 *
 * @param {Object} params
 * @param {Object|null} params.masterProduct
 * @param {Object} [params.selectedVariationAttributes]
 * @param {GalleryLayer[]} [params.layers]
 * @param {import('../context/customize-journey-context').SelectionsMap|null|undefined} [params.selections]
 * @param {Object|null} [params.previewProduct]
 * @param {boolean} [params.isCommittedSetOrCustomize]
 * @param {boolean} [params.allowCylindo]
 * @returns {{ pimImages, cylindoId, cylindoFeatures, customerId, isReady }}
 */
export function resolveGalleryResource({
  masterProduct,
  selectedVariationAttributes = {},
  layers = [],
  selections,
  previewProduct = null,
  isCommittedSetOrCustomize = false,
  allowCylindo = true,
}) {
  const previewLayer =
    previewProduct && previewProduct !== masterProduct ? [buildLayerFromProduct(previewProduct)] : [];
  const allLayers = [...layers, ...previewLayer];
  const applyLayerPriority = shouldApplyGalleryLayerPriority({
    isCommittedSetOrCustomize,
    layers: allLayers,
    masterProduct,
    previewProduct,
    selections,
  });

  return resolveGalleryResourceCombined(
    masterProduct,
    selectedVariationAttributes,
    applyLayerPriority ? allLayers : [],
    {allowCylindo, applyLayerPriority}
  );
}

/**
 * Resolves ImageGalleryV2 props from master product + variant + option layers.
 *
 * Hero image priority:
 *   Cover selected (draft/live-preview):  variant PIM → master Cylindo + features → master PIM → placeholder
 *   Cover selected (committed set/customize): master Cylindo + features → variant PIM → master PIM → placeholder
 *   No cover:                              master PIM → master Cylindo → placeholder
 *
 * Cover/option `cylindoId` is never used as the Cylindo product code (DE-790).
 *
 * Customize option layers may supply PIM when the hero tiers above are empty.
 * Dedicated `video` on preview layers remains exclusive when present.
 *
 * @param {Object|null}    masterProduct
 * @param {Object}         [selectedVariationAttributes={}]
 * @param {GalleryLayer[]} [layers=[]]  ordered lowest → highest; preview is last
 * @param {Object}         [options={}]
 * @param {boolean}        [options.allowCylindo=true]
 * @param {boolean}        [options.applyLayerPriority=true] — false: hero PIM/Cylindo only (no layer stack)
 * @param {boolean}        [options.preferVariantCylindo=false] — committed set/customize: configured Cylindo (master code + features) wins over cover PIM
 * @returns {{ pimImages, cylindoId, cylindoFeatures, customerId, isReady }}
 */
export function resolveGalleryResourceCombined(
  masterProduct,
  selectedVariationAttributes = {},
  layers = [],
  {allowCylindo = true, applyLayerPriority = true, preferVariantCylindo = false} = {}
) {
  if (!masterProduct) return {cylindoFeatures: [], isReady: false};

  const masterImageGroups = parseProcessedImageGroups(masterProduct);
  // Mount ImageGalleryV2 even when no image groups exist — V2 renders the placeholder.
  const isReady = true;

  const keys = Object.keys(selectedVariationAttributes).filter(
    (k) => selectedVariationAttributes[k] != null && selectedVariationAttributes[k] !== ''
  );

  const variantPimImages = keys.length > 0 ?
    findVariantPimImages(masterProduct, selectedVariationAttributes) :
    undefined;

  let masterPimImages = findMasterHeroStillImages(masterImageGroups, {
    allowCatalogFallback: !allowCylindo,
  });

  if (!masterPimImages?.length && !allowCylindo && Array.isArray(masterProduct.imageGroups)) {
    masterPimImages = findMasterHeroStillImages(masterProduct.imageGroups, {
      allowCatalogFallback: true,
    });
  }
  const customerId = resolveGalleryCustomerId(masterProduct, allowCylindo);

  // Cylindo features: master → variant → layers (later wins per key)
  const masterFeatures = parseCylindoFeatureAttribute(masterProduct);
  let variantFeatureStr = findVariantCylindoFeature(masterProduct, selectedVariationAttributes);

  const variantFeatures = variantFeatureStr ?
    parseCylindoFeatureAttribute({c_cylindoFeature: variantFeatureStr}) :
    [];

  let combinedFeaturesStr = mergeCylindoConfiguration(masterFeatures, variantFeatures);

  for (const layer of layers) {
    if (layer?.cylindoFeature == null || layer?.cylindoFeature === '') continue;

    const layerFeatures = parseCylindoFeatureAttribute({c_cylindoFeature: layer.cylindoFeature});

    if (layerFeatures.length === 0) continue;

    const currentFeatures = combinedFeaturesStr ?
      combinedFeaturesStr.split(',').map((s) => s.trim()).filter(Boolean) :
      [];

    combinedFeaturesStr = mergeCylindoConfiguration(currentFeatures, layerFeatures);
  }

  const cylindoFeatures = combinedFeaturesStr ?
    combinedFeaturesStr.split(',').map((s) => s.trim()).filter(Boolean) :
    [];

  const dedicatedVideoUrl = applyLayerPriority ?
    resolveDedicatedVideoUrlFromLayers(layers) :
    getProductVideoUrl(masterProduct);

  return finalizeHeroGalleryResource({
    allowCylindo,
    applyLayerPriority,
    cylindoFeatures,
    customerId,
    dedicatedVideoUrl,
    hasVariantCylindoFeature: variantFeatures.length > 0,
    isReady,
    keys,
    layers: applyLayerPriority ? layers : [],
    masterProduct,
    variantPimImages,
    masterPimImages,
    preferVariantCylindo,
  });
}

/**
 * Same resolution as inline gallery with no preview row: master + cover variation + committed option layers.
 *
 * @param {Object|null} masterProduct
 * @param {import('../context/customize-journey-context').SelectionsMap|null|undefined} selections
 * @param {{ allowCylindo?: boolean }} [options]
 * @returns {ReturnType<typeof resolveGalleryResourceCombined>}
 */
export function resolveGalleryResourceFromCommittedSelections(masterProduct, selections, options = {}) {
  if (!masterProduct || !selections || typeof selections !== 'object') {
    return {cylindoFeatures: [], isReady: false};
  }

  const variationValues =
    selections.cover && typeof selections.cover.variationValues === 'object' && selections.cover.variationValues !== null ?
      selections.cover.variationValues :
      {};

  const layers = buildLayersFromSelections(selections);

  return resolveGalleryResourceCombined(masterProduct, variationValues, layers, {
    allowCylindo: options.allowCylindo !== false,
    preferVariantCylindo: true,
  });
}

/**
 * Final hero visual URL after commit (Customize or predefined set): matches PDP hero tiers (PIM before Cylindo at master tier).
 *
 * @param {Object|null} masterProduct
 * @param {import('../context/customize-journey-context').SelectionsMap|null|undefined} selections
 * @param {{
 *   allowCylindo?: boolean,
 *   cylindoSnapshotOptions?: { size?: number, encoding?: 'webp'|'png'|'jpg', background?: string }
 * }} [options]
 * @returns {{
 *   url: string|null,
 *   mode: 'pim'|'cylindo'|'pim-fallback'|'none',
 *   cylindoConfiguration: string,
 *   galleryResource: ReturnType<typeof resolveGalleryResourceCombined>
 * }}
 */
export function resolveCommittedSelectionHeroVisualUrl(masterProduct, selections, options = {}) {
  const galleryResource = resolveGalleryResourceFromCommittedSelections(masterProduct, selections, options);
  const cylindoConfiguration = mergeCylindoConfiguration([], galleryResource.cylindoFeatures ?? []);

  const hasPim = Array.isArray(galleryResource.pimImages) && galleryResource.pimImages.length > 0;
  const hasCylindo =
    Boolean(galleryResource.cylindoId && galleryResource.customerId) &&
    (options.allowCylindo !== false);

  let result;

  if (hasPim) {
    const first = galleryResource.pimImages[0];
    const url = first?.disBaseLink || first?.link || null;

    result = {url, mode: 'pim', cylindoConfiguration, galleryResource};
  } else if (hasCylindo) {
    const url = buildCylindoSnapshotImageUrl(
      galleryResource.cylindoId,
      galleryResource.customerId,
      cylindoConfiguration || undefined,
      options.cylindoSnapshotOptions ?? {}
    );

    result = {url, mode: 'cylindo', cylindoConfiguration, galleryResource};
  } else {
    // Fallback: master product's first PIM image (from processed image groups or raw imageGroups)
    const masterFallbackUrl =
      galleryResource.pimImages?.[0]?.disBaseLink ||
      galleryResource.pimImages?.[0]?.link ||
      masterProduct?.imageGroups?.[0]?.images?.[0]?.disBaseLink ||
      masterProduct?.imageGroups?.[0]?.images?.[0]?.link ||
      masterProduct?.image?.disBaseLink ||
      masterProduct?.image?.link ||
      null;

    result = masterFallbackUrl ?
      {url: masterFallbackUrl, mode: 'pim-fallback', cylindoConfiguration, galleryResource} :
      {url: null, mode: 'none', cylindoConfiguration, galleryResource};
  }

  return result;
}

/**
 * Configured product hero image — set cards, cart (`cylindoUrl`), upsell.
 * Cover variant + option layers (same as PDP gallery after commit).
 *
 * @param {Object|null} masterProduct
 * @param {import('../context/customize-journey-context').SelectionsMap|null|undefined} selections
 * @param {{ requireCover?: boolean, mergePreselects?: boolean }} [options]
 * @returns {string|null}
 */
export function resolveCustomizeCartItemImageUrl(masterProduct, selections, options = {}) {
  const {requireCover = true, mergePreselects = false} = options;

  if (!masterProduct || !selections) {
    return null;
  }

  const resolvedSelections = mergePreselects ?
    mergePreselectedSelectionsFromProduct(masterProduct, selections) :
    selections;

  if (requireCover && !resolvedSelections.cover) {
    return null;
  }

  return resolveCommittedSelectionHeroVisualUrl(masterProduct, resolvedSelections).url;
}

/**
 * Committed PDP configuration at add-to-cart (predefined set or customize-your-own).
 *
 * @param {Object|null} masterProduct
 * @param {import('../context/customize-journey-context').SelectionsMap|null|undefined} selections
 * @param {{ requireCover?: boolean }} [options]
 * @returns {string|null}
 */
export function resolveCommittedConfiguredImageUrl(masterProduct, selections, options = {}) {
  return resolveCustomizeCartItemImageUrl(masterProduct, selections, {
    mergePreselects: true,
    requireCover: options.requireCover ?? true,
  });
}

/**
 * ImageGalleryV2 resource for committed configuration (set card, cart, upsell, main PDP gallery).
 *
 * @param {Object|null} masterProduct
 * @param {import('../context/customize-journey-context').SelectionsMap|null|undefined} selections
 * @param {{ allowCylindo?: boolean, mergePreselects?: boolean }} [options]
 * @returns {ReturnType<typeof resolveGalleryResourceFromCommittedSelections>}
 */
export function resolveCommittedSelectionGalleryResource(masterProduct, selections, options = {}) {
  const {mergePreselects = true, ...resolveOptions} = options;

  if (!masterProduct || !selections) {
    return {cylindoFeatures: [], isReady: false};
  }

  const resolvedSelections = mergePreselects ?
    mergePreselectedSelectionsFromProduct(masterProduct, selections) :
    selections;

  return resolveGalleryResourceFromCommittedSelections(masterProduct, resolvedSelections, resolveOptions);
}
